import type { ContentSummary } from '@hedgehog/shared'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
// The site's own link rule and path helpers, so the editor checks links exactly as the site does.
import { MAX_SECTIONS, SAFE_URL, pathForSlug, slugForPath } from '@hedgehog/framework/site/format'

export { MAX_SECTIONS, SAFE_URL, pathForSlug, slugForPath }

/** Hint under an address field: what SAFE_URL accepts. */
export const LINK_HINT = 'Start with https://, http://, mailto:, tel:, sms:, / for a page of this site, or # for a place on the page.'

const OTHER = '__other__'

/**
 * A link target: one of the site's pages, or another address typed in. `id` labels the page
 * select; the address input gets `${id}-url`. `error` is shown under the input and marks it invalid.
 */
export function LinkField({ id, value, onChange, pages, error, label = 'Link to' }: { id: string; value: string; onChange: (url: string) => void; pages: ContentSummary[]; error?: string | null; label?: string }) {
  // A page link may have no trailing slash ("/about"); both open the same page.
  const bare = (u: string) => u.trim().replace(/(.)\/+$/, '$1')
  const page = pages.find((p) => bare(pathForSlug(p.slug)) === bare(value))
  const other = !page
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <NativeSelect id={id} aria-describedby={`${id}-hint`} value={page ? page.slug : OTHER} onChange={(e) => onChange(e.target.value === OTHER ? (other ? value : 'https://') : pathForSlug(e.target.value))} className="w-full">
        {pages.map((p) => (
          <NativeSelectOption key={p.id} value={p.slug}>
            {p.title || pathForSlug(p.slug)}
          </NativeSelectOption>
        ))}
        <NativeSelectOption value={OTHER}>Another address…</NativeSelectOption>
      </NativeSelect>
      {other && (
        <>
          <label htmlFor={`${id}-url`} className="sr-only">
            Address
          </label>
          <Input
            id={`${id}-url`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            inputMode="url"
            maxLength={2000}
            placeholder="https://"
            aria-invalid={!!error || undefined}
            aria-describedby={`${id}-hint`}
          />
        </>
      )}
      <p id={`${id}-hint`} className={error ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
        {error ?? (page ? `Opens ${pathForSlug(page.slug)}` : LINK_HINT)}
      </p>
    </div>
  )
}

/** Error message for a link target, or null when the site accepts it. */
export function linkError(url: string): string | null {
  const u = url.trim()
  if (!u || u === 'https://' || u === 'http://') return 'Enter an address.'
  return SAFE_URL.test(u) ? null : LINK_HINT
}
